import { apiClient } from '@/lib/api-client';

export const platformBackupsKey = ['platform-backups'] as const;

export interface PlatformBackupRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'succeeded' | 'failed';
  manual: boolean;
  database_bytes: number;
  uploads_bytes: number;
  total_bytes: number;
  /** Whether this run reached the off-site copy. Tracked apart from `status` — see below. */
  offsite: boolean;
  offsite_error: string;
  error: string;
}

export interface PlatformBackupSettings {
  enabled: boolean;
  /** Minutes past midnight UTC. 210 is 03:30. */
  run_at_minute: number;
  keep_days: number;
  s3_enabled: boolean;
  s3_endpoint: string;
  s3_region: string;
  s3_bucket: string;
  s3_prefix: string;
  s3_access_key_id: string;
  /** Whether a secret is stored. The secret itself never leaves the server. */
  s3_secret_set: boolean;
}

export interface PlatformBackupOverview {
  settings: PlatformBackupSettings;
  last_successful_at: string | null;
  days_since_success: number | null;
  running: boolean;
  backups: PlatformBackupRun[];
}

export interface PlatformBackupSettingsInput extends Omit<PlatformBackupSettings, 's3_secret_set'> {
  /** Omitted or blank leaves the stored secret alone — it cannot be read back to prefill. */
  s3_secret_key?: string;
}

export const platformBackupsApi = {
  overview: () => apiClient.get<PlatformBackupOverview>('/admin/platform-backups'),

  run: () => apiClient.post<PlatformBackupRun>('/admin/platform-backups/run', {}),

  saveSettings: (input: PlatformBackupSettingsInput) =>
    apiClient.patch<PlatformBackupOverview>('/admin/platform-backups/settings', input),

  downloadPath: (id: string, file: 'database' | 'uploads') =>
    `/admin/platform-backups/${id}/download?file=${file}`,
};

/** "03:30" from minutes past midnight, and back. */
export function minuteToTime(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function timeToMinute(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());

  if (match === null) {
    return null;
  }

  const h = Number.parseInt(match[1] ?? '', 10);
  const m = Number.parseInt(match[2] ?? '', 10);

  if (h < 0 || h > 23 || m < 0 || m > 59) {
    return null;
  }

  return h * 60 + m;
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;

  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
